// Shared "Change organization" dropdown: one menu with three trigger shapes ('setup' row, 'pill',
// 'chip'). Choosing an org goes through OrgScopeService.switchOrg, the same path as the top-bar selector.
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  HostListener,
  inject,
  input,
  signal
} from '@angular/core'
import { animate, style, transition, trigger } from '@angular/animations'
import { OrgScopeService } from './org-scope.service'
import { PaOrgOption, PaOrgPickerVariant } from './org-scope.types'
import { PREFERS_REDUCED_MOTION } from '../ui/pa-reduced-motion'

@Component({
  selector: 'aiomed-pa-org-picker',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Enter-only and opacity-only: a fade-out on close reads as lag, and the pill menu is centered with a
  // transform that a transform animation would fight.
  animations: [
    trigger('menuFade', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('150ms ease-out', style({ opacity: 1 }))
      ])
    ])
  ],
  templateUrl: './pa-org-picker.component.html',
  styleUrls: ['./pa-org-picker.component.scss']
})
export class PaOrgPickerComponent {
  private readonly orgScope = inject(OrgScopeService)
  private readonly host = inject(ElementRef<HTMLElement>)

  /** Which trigger shape/anchor to render. Defaults to the home chip. */
  readonly variant = input<PaOrgPickerVariant>('chip')

  // Reduced motion disables the menu fade.
  protected readonly reduceMotion = PREFERS_REDUCED_MOTION
  protected readonly open = signal(false)
  protected readonly orgs = computed(() => this.orgScope.orgOptions())
  protected readonly activeId = this.orgScope.activeOrgId
  protected readonly activeName = computed(
    () => this.orgScope.activeOrgName() || 'Your organization'
  )

  /** The org rows, exposed for a host that needs them. */
  readonly orgOptions: () => PaOrgOption[] = () => this.orgScope.orgOptions()

  /** Design meta uses a hyphen separator ("City, ST - N units"); the service joins with a middot. */
  protected displayMeta(meta: string): string {
    return meta.replace(/ · /g, ' - ')
  }

  protected toggle(): void {
    this.open.update(v => !v)
  }

  protected choose(orgId: string): void {
    this.open.set(false)
    if (orgId !== this.activeId()) {
      this.orgScope.switchOrg(orgId)
    }
  }

  @HostListener('document:click', ['$event'])
  protected onDocClick(event: MouseEvent): void {
    const target = event.target as Node | null
    // Skip clicks whose target already left the DOM: opening the setup variant swaps its "Change" label
    // out synchronously, so that same click would arrive here as "outside" and close the menu it opened.
    if (
      this.open() &&
      target?.isConnected &&
      !this.host.nativeElement.contains(target)
    ) {
      this.open.set(false)
    }
  }

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    this.open.set(false)
  }
}
